import Spinner from "@src/lib/ui/Spinner";
import gql from "graphql-tag";
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Input } from "@src/lib/ui/Input";
import { GraphqlOperations } from "@src/types.generated";
import useUser from "@src/features/app/hooks/useUser";
import { useFeatureFlagsQuery } from "@src/lib/contexts/FeatureFlagContext.generated";
import Fuse from "fuse.js";
import {
  AdminToolsPanel,
  AdminToolsEmptyRow,
  AdminToolsRowList,
  AdminToolsToggleRow,
} from "@src/features/app/top-nav/AdminToolsLayout";
import { useUpdateEmployeeFeatureFlagsMutation } from "@src/features/app/top-nav/SetEmployeeFeatureFlags.generated";

const flagDisplayName = (f: { name: string; userFacingName?: string | null }) =>
  f.userFacingName || f.name;

const SetEmployeeFeatureFlags: React.FC = () => {
  const { data, loading } = useFeatureFlagsQuery();
  const { isStaff } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [updateEmployeeFeatureFlagsMutation] =
    useUpdateEmployeeFeatureFlagsMutation({
      refetchQueries: [GraphqlOperations.Query.FeatureFlags],
    });

  const updateEmployeeFeatureFlags = useCallback(
    (name: string, enabled: boolean) => {
      updateEmployeeFeatureFlagsMutation({
        variables: {
          input: {
            featureFlags: [
              {
                name,
                enabled: { value: enabled },
              },
            ],
          },
        },
      });
    },
    [updateEmployeeFeatureFlagsMutation],
  );

  const featureFlags = useMemo(() => {
    if (!data?.FeatureFlags) return [];
    return [...data.FeatureFlags].sort((a, b) =>
      flagDisplayName(a).localeCompare(flagDisplayName(b)),
    );
  }, [data?.FeatureFlags]);

  const fuse = useMemo(
    () =>
      new Fuse(featureFlags, {
        keys: ["name", "userFacingName"],
        threshold: 0.3,
      }),
    [featureFlags],
  );

  const filteredFeatureFlags = useMemo(() => {
    if (!searchTerm) return featureFlags;
    return fuse.search(searchTerm).map((result) => result.item);
  }, [fuse, searchTerm, featureFlags]);

  const featureFlagToggleRows = useMemo(() => {
    return filteredFeatureFlags.map((f) => (
      <AdminToolsToggleRow
        key={f.name}
        title={flagDisplayName(f)}
        checked={f.enabled}
        onCheckedChange={(checked) =>
          updateEmployeeFeatureFlags(f.name, checked)
        }
      />
    ));
  }, [filteredFeatureFlags, updateEmployeeFeatureFlags]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  if (!isStaff) return null;

  return (
    <AdminToolsPanel
      toolbar={
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="Search feature flags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      }
    >
      {loading ? (
        <div className="px-6 py-4">
          <Spinner />
        </div>
      ) : (
        <AdminToolsRowList>
          {featureFlagToggleRows.length > 0 ? (
            featureFlagToggleRows
          ) : (
            <AdminToolsEmptyRow>No feature flags found</AdminToolsEmptyRow>
          )}
        </AdminToolsRowList>
      )}
    </AdminToolsPanel>
  );
};

gql`
  mutation UpdateEmployeeFeatureFlags(
    $input: UpdateEmployeeFeatureFlagsInput!
  ) {
    updateEmployeeFeatureFlags(input: $input) {
      success
    }
  }
`;

export default SetEmployeeFeatureFlags;
